<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-card" role="dialog" aria-modal="true">
      <header class="modal-header">
        <h2>Forecast — {{ year }}</h2>
        <div class="modal-header-actions">
          <label class="year-picker">
            <span>Year</span>
            <select v-model.number="year">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </label>
          <button type="button" class="close-x" @click="close" aria-label="Close">×</button>
        </div>
      </header>

      <div class="modal-body">
        <p class="muted small">
          Projection from currently committed tenants, starting this month.
          <strong>New Teams</strong> turn paid when their trial / coupon
          period ends. <strong>Cancellations</strong> are pulled from Stripe
          (explicit cancel dates and "cancel at period end" flags).
          Free-tier tenants don't appear here.
        </p>

        <!-- KPI tiles -->
        <div class="kpi-row">
          <div class="kpi">
            <span class="kpi-label">Monthly Revenue</span>
            <span class="kpi-value">${{ formatNumber(monthlyRevenue) }}</span>
            <span class="kpi-sub">this month</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">Total Revenue</span>
            <span class="kpi-value">${{ formatNumber(totalRevenue) }}</span>
            <span class="kpi-sub">{{ horizonLabel }}</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">Month over Month</span>
            <span class="kpi-value" :class="momClass">
              {{ momAvg >= 0 ? '+' : '−' }}${{ formatNumber(Math.abs(momAvg)) }}/mo
              <span class="net-arrow">{{ momAvg >= 0 ? '▲' : '▼' }}</span>
            </span>
            <span class="kpi-sub">avg pace</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">Lost</span>
            <span class="kpi-value lost">${{ formatNumber(totalLost) }}</span>
            <span class="kpi-sub">{{ horizonLabel }}</span>
          </div>
        </div>

        <!-- MRR ladder -->
        <div class="ladder-section">
          <div class="ladder-title">MRR by month</div>
          <div class="ladder">
            <div
                v-for="row in monthlyRows"
                :key="row.month"
                class="ladder-row"
                :class="{ current: row.isCurrent }"
            >
              <div class="ladder-month">
                {{ row.shortLabel }}
                <span v-if="row.isCurrent" class="star" aria-label="current month">★</span>
              </div>
              <div class="ladder-bar" :title="`Recurring $${formatNumber(row.recurringDollars)} · New $${formatNumber(row.newDollars)}`">
                <div
                    class="ladder-seg recurring"
                    :style="{ width: pctOfMax(row.recurringDollars) + '%' }"
                ></div>
                <div
                    class="ladder-seg new"
                    :style="{ width: pctOfMax(row.newDollars) + '%' }"
                ></div>
              </div>
              <div class="ladder-amount">${{ formatNumber(row.totalDollars) }}</div>
              <div class="ladder-event">
                <span v-if="row.newTeams" class="event up">▲ +{{ row.newTeams }} {{ row.newTeams === 1 ? 'team' : 'teams' }}</span>
                <span v-if="row.cancellations" class="event down">
                  ▼ −{{ row.cancellations }} (−${{ formatNumber(row.lostDollars) }})
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Grouped detail table -->
        <div class="table-wrap">
          <table class="forecast-table">
            <thead>
              <tr class="group-header">
                <th></th>
                <th class="group" colspan="3">Teams</th>
                <th class="group" colspan="4">Monthly $</th>
              </tr>
              <tr>
                <th>Month</th>
                <th class="num">New</th>
                <th class="num">Paying</th>
                <th class="num">Cancel</th>
                <th class="num">New</th>
                <th class="num">Lost</th>
                <th class="num">Recur</th>
                <th class="num">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                  v-for="row in monthlyRows"
                  :key="row.month"
                  :class="{ current: row.isCurrent }"
              >
                <td>
                  {{ row.label }}
                  <span v-if="row.isCurrent" class="star">★</span>
                </td>
                <td class="num">{{ row.newTeams || '—' }}</td>
                <td class="num">{{ row.payingTeams || '—' }}</td>
                <td class="num">{{ row.cancellations || '—' }}</td>
                <td class="num">{{ row.newDollars ? `$${formatNumber(row.newDollars)}` : '—' }}</td>
                <td class="num lost">{{ row.lostDollars ? `−$${formatNumber(row.lostDollars)}` : '—' }}</td>
                <td class="num">{{ row.recurringDollars ? `$${formatNumber(row.recurringDollars)}` : '—' }}</td>
                <td class="num strong">${{ formatNumber(row.totalDollars) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total ({{ horizonLabel }})</strong></td>
                <td class="num"><strong>{{ totalNewTeams }}</strong></td>
                <td class="num">—</td>
                <td class="num"><strong>{{ totalCancellations }}</strong></td>
                <td class="num"><strong>${{ formatNumber(totalNew) }}</strong></td>
                <td class="num lost"><strong>−${{ formatNumber(totalLost) }}</strong></td>
                <td class="num"><strong>${{ formatNumber(totalRecurring) }}</strong></td>
                <td class="num strong"><strong>${{ formatNumber(totalRevenue) }}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <footer class="modal-footer">
        <button type="button" class="btn ghost" @click="exportCsv">Download CSV</button>
        <button type="button" class="btn primary" @click="close">Close</button>
      </footer>
    </div>
  </div>
</template>

<script>
// Convert anything the backend hands us (Firestore Timestamp, ISO string,
// Stripe seconds, JS ms) into a JS Date — or null when we can't.
function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v === 'number') {
    const ms = v > 1e12 ? v : v * 1000;
    const d = new Date(ms);
    return isNaN(d) ? null : d;
  }
  if (typeof v === 'object') {
    if (typeof v.toMillis === 'function') return new Date(v.toMillis());
    if (typeof v._seconds === 'number') return new Date(v._seconds * 1000);
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d) ? null : d;
  }
  return null;
}

function addMonths(d, n) {
  const out = new Date(d.getTime());
  out.setUTCMonth(out.getUTCMonth() + n);
  return out;
}

// Year/month integer index (e.g., 2026-05 → 2026*12+4).
function monthIndexOf(d) {
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
}

// First month Kiey actually collects money from this tenant. Returns null
// for free-forever tenants and tenants we can't price.
function firstPaidMonthIndex(row) {
  const monthly = Number(row.monthly_price_effective) || 0;
  if (!monthly) return null;
  if (row.pricing_mode === 'free') return null;

  const registered = toDate(row.signup_date);
  if (!registered) return null;

  let firstPaid = registered;
  const c = row.coupon;
  if (c && c.percent_off === 100) {
    if (c.duration === 'forever') return null;
    if (c.duration === 'repeating' && c.duration_in_months) {
      firstPaid = addMonths(registered, c.duration_in_months);
    }
    if (c.duration === 'once') {
      firstPaid = addMonths(registered, 1);
    }
  }
  return monthIndexOf(firstPaid);
}

// Month the team stops paying (no Recurring $ from the NEXT month onward).
// We treat the cancellation-effective month as still paying through that
// month (Stripe doesn't pro-rate by default), then they drop off.
function lastPaidMonthIndex(row) {
  const cancelAt = toDate(row.cancellation_effective_at);
  if (!cancelAt) return Infinity;
  return monthIndexOf(cancelAt);
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default {
  name: 'ForecastModal',
  props: {
    show: { type: Boolean, required: true },
    rows: { type: Array, default: () => [] },
  },
  data() {
    const now = new Date();
    return {
      year: now.getUTCFullYear(),
    };
  },
  computed: {
    yearOptions() {
      const now = new Date().getUTCFullYear();
      return [now - 1, now, now + 1, now + 2];
    },
    currentMonthIdx() {
      const now = new Date();
      return now.getUTCFullYear() * 12 + now.getUTCMonth();
    },
    startMonth() {
      const now = new Date();
      if (this.year === now.getUTCFullYear()) return now.getUTCMonth();
      if (this.year > now.getUTCFullYear()) return 0;
      return now.getUTCMonth();
    },
    horizonLabel() {
      const first = this.monthlyRows[0];
      const last = this.monthlyRows[this.monthlyRows.length - 1];
      if (!first || !last) return `${this.year}`;
      return `${first.shortLabel} – ${last.shortLabel}`;
    },
    monthlyByTenant() {
      return this.rows.map((r) => ({
        firstPaidMonth: firstPaidMonthIndex(r),
        lastPaidMonth: lastPaidMonthIndex(r),
        monthly: Number(r.monthly_price_effective) || 0,
      }));
    },
    monthlyRows() {
      const result = [];
      for (let m = this.startMonth; m < 12; m++) {
        const monthIdx = this.year * 12 + m;

        let newTeams = 0;
        let payingTeams = 0;
        let cancellations = 0;
        let newDollars = 0;
        let recurringDollars = 0;
        let lostDollars = 0;

        for (const t of this.monthlyByTenant) {
          if (t.firstPaidMonth === null) continue;

          const startedThisMonth = t.firstPaidMonth === monthIdx;
          const startedBefore = t.firstPaidMonth < monthIdx;
          const stillPayingThisMonth = monthIdx <= t.lastPaidMonth;
          const cancelsThisMonth = t.lastPaidMonth === monthIdx;
          const hasStarted = t.firstPaidMonth <= monthIdx;

          if (startedThisMonth && stillPayingThisMonth) {
            newTeams += 1;
            newDollars += t.monthly;
          }
          if (hasStarted && stillPayingThisMonth) {
            payingTeams += 1;
            if (startedBefore) recurringDollars += t.monthly;
          }
          if (cancelsThisMonth && hasStarted) {
            cancellations += 1;
            lostDollars += t.monthly;
          }
        }

        result.push({
          month: m,
          label: `${MONTH_LABELS[m]} ${this.year}`,
          shortLabel: MONTH_LABELS[m],
          newTeams,
          payingTeams,
          cancellations,
          newDollars,
          recurringDollars,
          lostDollars,
          totalDollars: newDollars + recurringDollars,
          isCurrent: monthIdx === this.currentMonthIdx,
        });
      }
      return result;
    },
    totalNewTeams() {
      return this.monthlyRows.reduce((s, r) => s + r.newTeams, 0);
    },
    totalCancellations() {
      return this.monthlyRows.reduce((s, r) => s + r.cancellations, 0);
    },
    totalNew() {
      return this.monthlyRows.reduce((s, r) => s + r.newDollars, 0);
    },
    totalRecurring() {
      return this.monthlyRows.reduce((s, r) => s + r.recurringDollars, 0);
    },
    totalLost() {
      return this.monthlyRows.reduce((s, r) => s + r.lostDollars, 0);
    },
    totalRevenue() {
      return this.totalNew + this.totalRecurring;
    },
    /// Current month's MRR — the run rate you're collecting RIGHT NOW.
    /// Falls back to the first row in the window when the selected year
    /// isn't the current year.
    monthlyRevenue() {
      const cur = this.monthlyRows.find((r) => r.isCurrent);
      if (cur) return cur.totalDollars;
      return this.monthlyRows[0] ? this.monthlyRows[0].totalDollars : 0;
    },
    /// Average month-over-month increase in committed (on-the-books)
    /// monthly revenue across the forecast window. Math telescopes —
    /// summing consecutive diffs equals (last − first), so we just divide
    /// by the number of month-to-month transitions. Stays meaningful when
    /// the starting value is 0 (where a % growth figure would be ∞).
    momAvg() {
      if (this.monthlyRows.length < 2) return 0;
      const first = this.monthlyRows[0].totalDollars;
      const last = this.monthlyRows[this.monthlyRows.length - 1].totalDollars;
      return Math.round((last - first) / (this.monthlyRows.length - 1));
    },
    momClass() {
      if (this.momAvg > 0) return 'up';
      if (this.momAvg < 0) return 'lost';
      return '';
    },
    maxMonthDollars() {
      return Math.max(1, ...this.monthlyRows.map((r) => r.totalDollars));
    },
  },
  methods: {
    close() {
      this.$emit('update:show', false);
    },
    formatNumber(n) {
      if (typeof n !== 'number' || !isFinite(n)) return '0';
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
    },
    pctOfMax(v) {
      return Math.round((v / this.maxMonthDollars) * 100);
    },
    exportCsv() {
      const headers = [
        'Month', 'New Teams', 'Paying Teams', 'Cancellations',
        'New $', 'Lost $', 'Recurring $', 'Total $',
      ];
      const lines = [headers.join(',')];
      for (const r of this.monthlyRows) {
        lines.push([
          r.label, r.newTeams, r.payingTeams, r.cancellations,
          r.newDollars, r.lostDollars, r.recurringDollars, r.totalDollars,
        ].join(','));
      }
      lines.push([
        'Total', this.totalNewTeams, '', this.totalCancellations,
        this.totalNew, this.totalLost, this.totalRecurring, this.totalRevenue,
      ].join(','));
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forecast-${this.year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; z-index: 10010;
  background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
}
.modal-card {
  background: #fff; border-radius: 12px;
  width: 980px; max-width: 100%;
  max-height: calc(100vh - 64px); overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.18);
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #eef1f5;
}
.modal-header h2 { margin: 0; font-size: 16px; font-weight: 700; color: #061D47; }
.modal-header-actions { display: flex; align-items: center; gap: 20px; }
.year-picker { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280; }
.year-picker select {
  border: 1px solid #c2cbe0; border-radius: 6px;
  padding: 4px 8px; font-size: 13px; background: #fff;
}
.close-x {
  background: transparent; border: 0; cursor: pointer; font-size: 24px;
  color: #6b7280; line-height: 1; padding: 4px 8px;
}
.close-x:hover { color: #1f2937; }

.modal-body { padding: 16px 20px; overflow-y: auto; flex: 1; }
.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 20px; border-top: 1px solid #eef1f5; background: #fafbfd;
}
.muted { color: #6b7280; }
.small { font-size: 13px; }

/* KPI tiles */
.kpi-row {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
  margin: 16px 0;
}
.kpi {
  display: flex; flex-direction: column; gap: 4px;
  padding: 12px 14px; border: 1px solid #eef1f5; border-radius: 8px;
  background: #fafbfd;
}
.kpi-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.kpi-value { font-size: 20px; font-weight: 700; color: #061D47; }
.kpi-value.up { color: #047857; }
.kpi-value.lost { color: #b91c1c; }
.kpi-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
.net-arrow { font-size: 14px; }

/* MRR ladder */
.ladder-section { margin: 20px 0 24px; }
.ladder-title {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
  color: #6b7280; margin-bottom: 8px;
}
.ladder {
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px 14px; border: 1px solid #eef1f5; border-radius: 8px;
  background: #fafbfd;
}
.ladder-row {
  display: grid;
  grid-template-columns: 60px minmax(120px, 1fr) 90px minmax(160px, 240px);
  align-items: center; gap: 12px;
  font-size: 13px;
}
.ladder-row.current { font-weight: 600; }
.ladder-month { color: #1f2937; font-variant-numeric: tabular-nums; }
.ladder-bar {
  display: flex; height: 14px; border-radius: 4px;
  background: #eef1f5; overflow: hidden;
}
.ladder-seg { height: 100%; transition: width 0.2s ease; }
.ladder-seg.recurring { background: #0b3a8a; }
.ladder-seg.new       { background: #2ec27e; }
.ladder-amount {
  text-align: right; color: #061D47; font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.ladder-event { display: flex; gap: 10px; font-size: 12px; }
.event.up   { color: #047857; }
.event.down { color: #b91c1c; }
.star { color: #d4a017; font-size: 12px; margin-left: 4px; }

/* Detail table */
.table-wrap { overflow-x: auto; border: 1px solid #eef1f5; border-radius: 8px; }
.forecast-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
}
.forecast-table th, .forecast-table td {
  padding: 8px 12px; border-bottom: 1px solid #eef1f5;
  text-align: left;
}
.forecast-table tr.group-header th {
  border-bottom: 1px solid #eef1f5; background: #f3f4f6;
}
.forecast-table th.group {
  text-align: center; font-size: 11px; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;
}
.forecast-table thead tr:nth-child(2) th {
  background: #fafbfd; font-size: 11px; text-transform: uppercase;
  color: #6b7280; letter-spacing: 0.04em;
}
.forecast-table tr.current { background: #fdf6e3; }
.forecast-table tr.current td { font-weight: 600; }
.forecast-table tfoot td {
  background: #fafbfd; border-top: 2px solid #eef1f5;
  border-bottom: 0;
}
.num { text-align: right; font-variant-numeric: tabular-nums; }
.num.lost { color: #b91c1c; }
.strong { font-weight: 700; color: #061D47; }

.btn {
  border: 0; border-radius: 6px;
  padding: 8px 16px; font-size: 14px; font-weight: 600;
  cursor: pointer;
}
.btn.ghost { background: transparent; color: #4b5563; }
.btn.ghost:hover { background: #eef1f5; }
.btn.primary { background: #061D47; color: #fff; }
.btn.primary:hover { background: #0b2a66; }
</style>
